const xe = {
  id: "xe",
  label: "XE",
  tabDescription: "Scaling a Global Digital Ecosystem",
  title: "Scaling a Global Digital Ecosystem",
  sections: [
    {
      id: "mandate",
      title: "Mandate",
      paragraphs: [
        "When I joined XE in 2009, it was a lean, entrepreneurial company already well known for its currency-conversion services and serving approximately 1.8 million people each day through its website and early mobile presence.",
        "Over the following decade, customer behaviour shifted rapidly toward mobile, the business expanded into new financial services, and XE became part of Euronet Worldwide. These changes created new opportunities, but also introduced greater technical, product, and organizational complexity.",
        "XE needed to mature from a successful collection of digital products into a connected ecosystem capable of serving millions of customers across web and mobile while supporting new services, global markets, and increasingly diverse user needs.",
        "As Director of UX & Design, my mandate included guiding the mobile-first re-architecture of the website, directing the continued evolution of the XE Currency app, establishing the UX practice, introducing customer research and product-testing programs, creating XE’s design system, integrating accessibility into product development, and strengthening collaboration across Product, Technology, Marketing, Customer Support, and the wider Euronet organization.",
        "The work had to modernize the organization without disrupting the products, revenue, and customer trust already sustaining the business.",
      ],
    },
    {
      id: "strategy",
      title: "Strategy",
      paragraphs: [
        "A conventional, large-scale redesign would have created significant risk for a small organization operating a high-traffic global platform. XE could not pause product delivery or replace its digital ecosystem all at once without disrupting services used by millions of customers. Instead, I pursued a strategy of progressive modernization.",
        "I identified the next customer, organizational, or technical constraint limiting progress, while also looking ahead to the changes likely to reshape the business. Each initiative addressed an immediate need, but was planned to create the flexibility, skills, and technical foundations XE would require next.",
        "This began by establishing UX as a working partner to Engineering and Marketing. It then expanded progressively into modular front-end architecture, responsive design, mobile products, customer research, accessibility, design systems, internationalization, and shared implementation standards.",
        "As XE expanded into new financial services and later became part of Euronet Worldwide, the same strategy helped the organization absorb greater complexity. Practices that had begun inside a lean entrepreneurial company could be formalized, extended across distributed teams, and applied more broadly without losing the pragmatism that had made them effective.",
      ],
    },
  ],
  capabilities: [
    {
      id: "platform-modernization",
      title: "Platform Modernization & Product Leadership",
      description:
        "Anticipated major shifts in customer behaviour and digital technology, then progressively modernized XE’s highest-traffic experiences to prepare the business for mobile adoption, new financial services, and continued global growth.",
      contributions: [
        "Established UX as a collaborative partner to Engineering, Marketing, Product, and executive leadership.",
        "Guided the incremental modernization of XE.com instead of pursuing a disruptive platform replacement.",
        "Led the mobile-first and responsive re-architecture of XE.com.",
        "Connected customer needs, analytics, commercial opportunities, and technical realities to product planning.",
        "Helped extend XE beyond currency information into money transfer, premium products, and emerging financial services.",
      ],
    },
    {
      id: "mobile-product-innovation",
      title: "Mobile Product Innovation",
      description:
        "Led the creation and continuous evolution of the XE Currency app, helping transform a simple conversion utility into one of the world’s most widely adopted currency applications.",
      contributions: [
        "Led UX across iOS, Android, Windows, phones, tablets, watches, and emerging device platforms.",
        "Rebuilt the application’s information architecture and introduced Smart Search.",
        "Introduced and refined Rate Alerts, historical charts, offline access, and personalization concepts.",
        "Worked directly with Apple and Microsoft engineers on emerging-platform implementations.",
        "Supported growth beyond 80 million installs while maintaining ratings above four stars.",
      ],
    },
    {
      id: "design-systems-operations",
      title: "Design Systems & UX Operations",
      description:
        "Established shared design and implementation foundations that improved consistency, centralized assets, and strengthened collaboration between Design and Engineering.",
      contributions: [
        "Created XE’s design system, digital style and UX guide, reusable UI kits, and a React component library.",
        "Centralized assets and standards that had previously been distributed across products and teams.",
        "Introduced reusable implementation patterns that reduced duplicated design and front-end effort.",
        "Created common documentation for UX, UI Engineering, Product, and Technology.",
      ],
    },
    {
      id: "research-product-insight",
      title: "Research, Testing & Product Insight",
      description:
        "Developed XE’s research capability from direct customer feedback into structured qualitative and quantitative practices that informed product strategy and delivery.",
      contributions: [
        "Introduced interviews, usability testing, surveys, concept validation, analytics reviews, and beta feedback.",
        "Built and managed a global customer research panel of approximately 3,000 participants.",
        "Connected behavioural data with direct customer insight.",
        "Helped establish continuous learning as part of product development.",
      ],
    },
    {
      id: "accessibility-global-standards",
      title: "Accessibility, Global Standards & Compliance",
      description:
        "Established accessibility and global experience practices that supported inclusive delivery, regulatory expectations, and customers across languages, regions, devices, and abilities.",
      contributions: [
        "Integrated accessibility into UX, design, development, and quality practices.",
        "Embedded WCAG considerations into shared components and responsive experiences.",
        "Supported site mirroring and right-to-left layouts for languages including Arabic.",
        "Helped establish practices that later contributed to a broader Euronet Accessibility Centre of Excellence.",
      ],
    },
    {
      id: "team-organizational-capability",
      title: "Team & Organizational Capability",
      description:
        "Expanded XE’s UX capability from an individual design role into a multidisciplinary practice supporting a global portfolio and distributed teams.",
      contributions: [
        "Established and grew XE’s UX team across product design, research, accessibility, and UI engineering.",
        "Mentored designers and encouraged greater ownership, experimentation, and product thinking.",
        "Expanded leadership across teams in Canada, the United Kingdom, Australia, New Zealand, and the United States.",
        "Positioned UX as a contributor to product and business strategy.",
      ],
    },
  ],
  outcomes: [
    {
      title: "Global Reach & Product Adoption",
      description:
        "XE’s digital ecosystem grew from approximately 1.8 million to more than 3 million daily users across web and mobile. The XE Currency app exceeded 80 million installs and maintained ratings above four stars across major app stores.",
    },
    {
      title: "Digital & Commercial Performance",
      description:
        "The mobile-first responsive re-architecture helped preserve XE’s audience as customer behaviour shifted toward mobile. The work contributed to increased traffic, money-transfer activity, premium product adoption, and digital revenue while reducing website bounce rate by approximately 40%.",
    },
    {
      title: "A More Adaptable Product Foundation",
      description:
        "Modular front-end architecture, reusable components, shared design standards, responsive implementation, accessibility practices, and right-to-left support made the ecosystem easier to extend across products, devices, languages, and markets.",
    },
    {
      title: "Sustainable Organizational Capability",
      description:
        "XE developed from an engineering-led company without an established UX function into an organization with multidisciplinary design leadership, structured research, accessibility governance, design systems, UI engineering practices, and stronger cross-functional partnerships.",
    },
  ],
  reflection: {
    quote:
      "Successful organizations rarely transform through a single redesign or sweeping organizational initiative.",
    supporting:
      "Lasting change requires both a clear view of where customers, technology, and the market are heading, and the discipline to move the organization toward that future in stages it can realistically adopt. That experience shaped how I lead today: I look ahead to identify the capabilities an organization will need next, then work backward to remove constraints, build confidence, and create the foundations that make that future achievable.",
  },
};

export default xe;
